import { useEffect } from "react";
import type { ReactNode } from "react";
import { Outlet, useNavigate, useRouter } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Spinner } from "@erp/ui";

import { useSession } from "@/lib/session/use-session";
import { ScanSlotProvider } from "./scan-slot";
import { Sidebar } from "./sidebar";
import { Topbar } from "./topbar";

export interface AuthGuardProps {
  children: ReactNode;
}

/**
 * Gate for the authenticated area: full-page spinner while the session
 * probe is in flight, redirect to /login (remembering where the user was
 * headed) when anonymous, children otherwise.
 *
 * The redirect runs from an effect exactly once per anonymous resolution —
 * rendering `<Navigate>` here would re-fire on every rerender with the
 * then-current href and feed the transition back into itself.
 */
export function AuthGuard({ children }: AuthGuardProps) {
  const { t } = useTranslation();
  const { user, isLoading } = useSession();
  const router = useRouter();
  const navigate = useNavigate();
  const shouldRedirect = !isLoading && user === null;

  useEffect(() => {
    if (!shouldRedirect) return;
    void navigate({
      to: "/login",
      search: { redirect: router.state.location.href },
      replace: true,
    });
  }, [navigate, router, shouldRedirect]);

  if (isLoading || user === null) {
    return (
      <div className="flex h-dvh items-center justify-center bg-background">
        <Spinner size="lg" label={t("app.loading")} />
      </div>
    );
  }

  return <>{children}</>;
}

/** Authenticated shell: sidebar rail + global scanner slot + routed outlet. */
export function AppLayout() {
  return (
    <AuthGuard>
      <ScanSlotProvider>
        <div className="flex h-dvh overflow-hidden bg-background text-foreground">
          <Sidebar />
          <div className="flex min-w-0 flex-1 flex-col">
            <Topbar />
            <main className="flex-1 overflow-y-auto p-6">
              <Outlet />
            </main>
          </div>
        </div>
      </ScanSlotProvider>
    </AuthGuard>
  );
}
